import { forwardRef, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ICONS from "../../../assets/Icons";
import TEXT from "../../../utils/globalText";
import PrimaryButton from "../CommonButtons/PrimaryButton";
import SVGComponent from "../SVGComponent";
import "./index.css";
import { GlobalConfiguration } from "../../../utils/configurationProvider";

// -----------------------------------------------------------------------------
// Suggested Props for the DatePicker Component
// -----------------------------------------------------------------------------

/**
 * @typedef {"single" | "range"} SelectionType
 * Represents the selection behavior of the calendar:
 * - "single": allows selecting only one date
 * - "range": enables selecting a date range (start & end)
 */

/**
 * @typedef {"small" | "big"} CalendarSize
 * Controls the visual size and spacing of the calendar UI.
 */

/**
 * Props for the DatePicker component.
 *
 * @param {Object} props
 *
 * @param {Date | null} props.startDate
 * The selected **start date**.
 * - In **single selection** mode, this is the only selected date.
 * - In **range mode**, this represents the starting point of the range.
 *
 * @param {Date | null} [props.endDate]
 * The selected **end date**, only required when using **range** selection.
 * - If not provided, the component behaves as a single-date picker.
 *
 * @param {(date: Date | [Date, Date] | null | [null, null]) => void} props.handleDateChange
 * A callback function triggered whenever the user **confirms** or **clears**
 * a date. It is NOT called while the user is still picking dates.
 *
 * - In **single selection** mode:
 *   The function receives a **single Date** → `handleDateChange(Date)`
 *   (or `null` when cleared)
 *
 * - In **range selection** mode:
 *   The function receives an **array containing [startDate, endDate]**
 *   → `handleDateChange([startDate, endDate])`
 *   (or `[null, null]` when cleared)
 *
 * The consumer of this component should use these values to update their own
 * state (e.g., startDate / endDate).
 *
 * @param {Boolean} props.isFullWidthRequired
 *
 * @param {CalendarSize} props.type
 * Determines the calendar size/UI variant.
 * Examples: `"small"` for compact UI, `"big"` for full view.
 *
 * @param {SelectionType} [props.selectionType="single"]
 * Type of date selection:
 * - `"single"` → only one date can be selected
 * - `"range"` → allows selecting a start and end date
 *
 * @param {Date | null} [props.minDate]
 * Minimum selectable date.
 * - Use it to **disable past dates** (e.g., only allow future dates).
 * - If not provided, the calendar supports full date navigation.
 *
 * @param {Date | null} [props.maxDate]
 * Maximum selectable date.
 * - Use it to **disable future dates**.
 * - If not provided, it will take current date.
 * - If you want future dates to be enabled, pass null in it.
 *
 * -----------------------------------------------------------------------------
 * Selection behaviour (range mode)
 * -----------------------------------------------------------------------------
 *
 * 1. **Live preview**
 *    While the calendar is open, the header shows the selection in progress:
 *    - Nothing selected   → placeholders ("From date" / "To date")
 *    - Start date clicked → start date is shown, and "To date" shows the
 *                           current date (see 2)
 *    - Start date changed → the header updates to the new start date
 *    - End date clicked   → the clicked end date replaces the default
 *    Closing the calendar without confirming reverts the header to the last
 *    confirmed range.
 *
 * 2. **Only a start date selected (To date left blank)**
 *    The end date defaults to the **current date**
 *    (e.g. today = 5 Oct, start = 1 Oct → 1 Oct → 5 Oct).
 *    - If the start date is today or later, the end date equals the start date.
 *    - The end date never goes past `maxDate`.
 *    The parent receives `[startDate, endDate]` on Confirm.
 *    The Confirm button is enabled as soon as a start date exists.
 *
 *   3. **Date clicked earlier than the start date**
 *    The two dates are swapped automatically: the earlier (clicked) date
 *    becomes the start date and the previous start date becomes the end date.
 *    e.g. start = 1 Oct 2026, user then clicks 3 Aug → 3 Aug → 1 Oct.
 *    This only applies while the end date is still empty. Once both dates are
 *    selected, the next click starts a new range.
 *
 * 4. **Any date click syncs the view**
 *    The month label and the year dropdown move to the year/month of the
 *    date that was clicked, so the header and dropdown always match the
 *    selection.
 *
 * 5. **Clear**
 *    Resets both the pending and the confirmed dates, calls
 *    `handleDateChange([null, null])` (range) or `handleDateChange(null)`
 *    (single), and closes the calendar.
 *
 * 6. **Parent re-renders**
 *    Incoming `startDate` / `endDate` are compared by timestamp, so a parent
 *    that creates `new Date(...)` on every render does not reset a selection
 *    that is in progress.
 *
 * Single mode follows 1 (live preview), 4 and 5; there is no end date.
 *
 * @returns {JSX.Element} The rendered DatePicker component.
 */

const CommonCalendar = ({
  isOpen = false,
  startDate,
  endDate,
  handleDateChange,
  type,
  selectionType,
  minDate,
  maxDate = new Date(),
  isFullWidthRequired,
  expandWithinContainer = false,
  externalCloseSignal = GlobalConfiguration.lengthCheckZero,
  isDisabled = false,
}) => {
  const listRef = useRef(null);
  const isRange = selectionType === "range"; // If endDate exists ---> range
  const today = new Date();
  const effectiveMin = minDate || null; // if minDate passed ---> future only
  const effectiveMax = minDate ? null : maxDate === null ? null : maxDate;
  const datePickerRef = useRef(null);
  const yearDropdownRef = useRef(null);
  const dropdownOptionRef = useRef(null);

  const [dpKey, setDpKey] = useState(0);
  const [currentView, setCurrentView] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(isOpen);
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [hidePrev, setHidePrev] = useState(false);
  const [hideNext, setHideNext] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate || null);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate || null);
  const [yearDropdownTouched, setYearDropdownTouched] = useState(false);
  // const [openUpward, setOpenUpward] = useState(false);

  // Compare incoming dates by timestamp, not by object identity.
  // Parents often create `new Date(...)` on every render, which would
  // otherwise reset the user's in-progress selection on each re-render.
  const startTime = startDate instanceof Date ? startDate.getTime() : null;
  const endTime = endDate instanceof Date ? endDate.getTime() : null;

  const resolvedOpenDate =
    tempStartDate ||
    localStartDate ||
    (effectiveMin ? effectiveMin : new Date());

  useEffect(() => {
    updateNavigationVisibility(currentYear, currentMonth);
  }, [currentYear, currentMonth, effectiveMin, effectiveMax]);

  const getDayClass = (date) => {
    const currentMonth = new Date(currentView).getMonth();
    const dayMonth = date.getMonth();

    if (dayMonth !== currentMonth) {
      return "datepicker-day-faded"; // Past or future month → faded
    }

    return ""; // Normal days
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current?.contains(event.target)
      ) {
        setShowDatePicker(false);
        setOpenDropdown(null);
        setYearDropdownTouched(false); // user interacted with the dropdown
        setCurrentYear(today.getFullYear());
        // FORCE REMOUNT
        setDpKey((prev) => prev + 1);
        if (!startDate) {
          if (effectiveMin) {
            setCurrentView(effectiveMin);
            setCurrentMonth(effectiveMin.getMonth());
            setCurrentYear(effectiveMin.getFullYear());
          } else {
            resetToToday();
          }
        }
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Sync from parent only when the actual date values change
  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate || null);
    setLocalStartDate(startDate);
    setLocalEndDate(endDate || null);

    const refDate = endDate || startDate;
    if (refDate instanceof Date && !Number.isNaN(refDate.getTime())) {
      setCurrentView(refDate);
      setCurrentMonth(refDate.getMonth());
      setCurrentYear(refDate.getFullYear());
      updateNavigationVisibility(refDate.getFullYear(), refDate.getMonth());
    }
  }, [startTime, endTime]);

  // Opening/closing the calendar: discard any unconfirmed selection
  useEffect(() => {
    setTempStartDate(localStartDate);
    setTempEndDate(localEndDate);
  }, [showDatePicker]);

  useEffect(() => {
    setShowDatePicker(false);
    setOpenDropdown(null);
    setYearDropdownTouched(false);
  }, [externalCloseSignal]);

  useEffect(() => {
    if (openDropdown === null) {
      setYearDropdownTouched(false);
    }
  }, [openDropdown]);

  const updateNavigationVisibility = (year, month) => {
    let _hidePrev = false;
    let _hideNext = false;

    if (effectiveMin) {
      const minYear = effectiveMin.getFullYear();
      const minMonth = effectiveMin.getMonth();
      if (year < minYear || (year === minYear && month <= minMonth)) {
        _hidePrev = true;
      }
    }

    if (effectiveMax) {
      const maxYear = effectiveMax.getFullYear();
      const maxMonth = effectiveMax.getMonth();
      if (year > maxYear || (year === maxYear && month >= maxMonth)) {
        _hideNext = true;
      }
    }

    setHidePrev(_hidePrev);
    setHideNext(_hideNext);
  };

  const resetToStartDate = () => {
    if (!localStartDate) return;

    const date = new Date(localStartDate);

    setCurrentYear(date.getFullYear());
    setCurrentMonth(date.getMonth());
    setCurrentView(date);
  };

  const resetToToday = () => {
    const today = new Date();
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
    setCurrentView(today);
  };

  const handleMonthChange = (date) => {
    const newMonth = date.getMonth();
    const newYear = date.getFullYear();
    setCurrentView(date);
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    updateNavigationVisibility(newYear, newMonth);
  };

  // Keeps the month label + year dropdown in sync with the clicked date
  const syncViewToDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
    setCurrentView(date);
    setCurrentMonth(date.getMonth());
    setCurrentYear(date.getFullYear());
    updateNavigationVisibility(date.getFullYear(), date.getMonth());
  };

  const handleChange = (dates) => {
    if (isRange) {
      let [start, end] = dates;
      let clickedDate = end || start;

      // Only From was selected, and the user clicks an EARLIER date.
      // react-datepicker sends [clickedDate, null], so we swap:
      // clicked date -> From, previous From -> To
      // e.g. From = 1 Oct, click 3 Aug  =>  3 Aug -> 1 Oct
      if (
        start &&
        !end &&
        tempStartDate &&
        !tempEndDate &&
        start < tempStartDate
      ) {
        end = tempStartDate;
        clickedDate = start;
      }

      // Safeguard: if an end date still comes in earlier than start, swap them
      if (start && end && end < start) {
        [start, end] = [end, start];
      }

      setTempStartDate(start);
      setTempEndDate(end || null);
      syncViewToDate(clickedDate);
    } else {
      setTempStartDate(dates);
      syncViewToDate(dates);
    }
  };
  // From date only -> To date defaults to today.
  // Never earlier than From, never later than maxDate.
  const getAutoEndDate = (start) => {
    if (!start) return null;

    const now = new Date();
    let end = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (effectiveMax) {
      const max = new Date(
        effectiveMax.getFullYear(),
        effectiveMax.getMonth(),
        effectiveMax.getDate(),
      );
      if (max < end) end = max;
    }

    return end >= start ? end : new Date(start);
  };

  const handleConfirm = () => {
    if (!showDatePicker) return;

    // Nothing picked: just close
    if (!tempStartDate) {
      setShowDatePicker(false);
      setOpenDropdown(null);
      return;
    }

    // Range mode:
    // - user picked a To date -> use it
    // - To date left blank    -> default to today (see getAutoEndDate)
    const finalStart = tempStartDate;
    const finalEnd = isRange
      ? tempEndDate || getAutoEndDate(tempStartDate)
      : null;

    setShowDatePicker(false);
    setOpenDropdown(null);

    // Update real local state only here
    setTempEndDate(finalEnd);
    setLocalStartDate(finalStart);
    setLocalEndDate(finalEnd);

    // Send to parent
    if (isRange) {
      handleDateChange([finalStart, finalEnd]);
    } else {
      handleDateChange(finalStart);
    }
  };

  // const handleConfirm = () => {
  //   if (!showDatePicker) return;

  //   if (isRange && (!tempStartDate || !tempEndDate)) {
  //     setShowDatePicker(false);
  //     setOpenDropdown(null);
  //     return;
  //   }

  //   setShowDatePicker(false);
  //   setOpenDropdown(null);

  // Update real local state only here
  //   setLocalStartDate(tempStartDate);
  //   setLocalEndDate(tempEndDate);

  // Send to parent
  //   if (isRange) {
  //     handleDateChange([tempStartDate, tempEndDate]);
  //   } else {
  //     handleDateChange(tempStartDate);
  //   }
  // };

  const handleClear = () => {
    if (!tempStartDate) {
      setShowDatePicker(false);
      setOpenDropdown(null);
      return;
    }
    if (showDatePicker) {
      setOpenDropdown(null);
      setYearDropdownTouched(false); // user interacted with the dropdown

      if (isRange) {
        setTempStartDate(null);
        setTempEndDate(null);

        setLocalStartDate(null);
        setLocalEndDate(null);

        handleDateChange([null, null]);
      } else {
        setTempStartDate(null);
        handleDateChange(null);
        setLocalStartDate(null);
      }

      // Reset calendar view to today's date
      resetToToday();

      // FORCE REMOUNT
      setDpKey((prev) => prev + 1);

      setShowDatePicker(false);
    }
  };

  const formatDate = (date) => {
    return `${date.getDate()} ${date.toLocaleString("default", {
      month: "short",
    })}, ${date.getFullYear()}`;
  };

  const Dropdown = forwardRef(
    (
      {
        year, // current year value
        options, // array of { name: "2023", value: 2023 }
        selectedOption,
        setSelectedOption,
        isOpen,
        dropdownKey,
        setOpenDropdown,
        dropdownRef,
      },
      ref,
    ) => {
      useEffect(() => {
        if (isOpen && listRef.current) {
          const selectedEl = listRef.current.querySelector(
            `[data-value="${selectedOption?.value}"]`,
          );

          if (selectedEl) {
            listRef.current.scrollTop = selectedEl.offsetTop - 10;
          }
        }
      }, [isOpen, selectedOption]);

      useEffect(() => {
        const handleClickOutside = (event) => {
          if (
            dropdownRef.current &&
            !dropdownRef.current?.contains(event.target)
          ) {
            setOpenDropdown(null);
            setYearDropdownTouched(false); // user interacted with the dropdown
          }
        };
        document.addEventListener("click", handleClickOutside);
        return () => {
          document.removeEventListener("click", handleClickOutside);
        };
      }, []);
      return (
        <div
          ref={dropdownRef}
          className={`flex flex-col relative w-year-dropdown-width flex-[1] bg-primary-nav ${!isOpen ? "rounded-border-radius-md " : " rounded-b-none rounded-t-border-radius-md"}`}
        >
          {/* Header */}
          <div
            className={`flex flex-col ${!isOpen ? "global-hover-white relative z-z-index-content rounded-border-radius-md " : " rounded-b-none"}`}
          >
            <div
              className={`flex justify-between items-center py-padding-sm cursor-pointer border-b-border ${
                isOpen
                  ? "border-b-primary-bg mx-margin-md "
                  : "px-padding-md w-width-full border-b-[transparent]"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setYearDropdownTouched(true); // user interacted with the dropdown
                setOpenDropdown(isOpen ? null : dropdownKey);
              }}
            >
              {/* Selected year text */}
              <div className="flex items-center">
                <div
                  className={`text-font-xs text-text-primary whitespace-nowrap overflow-ellipsis overflow-hidden w-width-full`}
                >
                  {selectedOption?.name}
                </div>
              </div>

              {/* Arrow Icons */}
              <div className="flex gap-gap-sm justify-between h-[-webkit-fill-available]">
                <div className="flex items-center h-height-full">
                  <SVGComponent
                    url={
                      isOpen ? ICONS?.ARROW_UP_WHITE : ICONS?.ARROW_DOWN_WHITE
                    }
                    color={"var(--text-primary)"}
                    height={"var(--icon-xs)"}
                    width={"var(--icon-xs)"}
                    alt="Arrow"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Dropdown List */}
          <div className="w-width-full absolute top-top-full bg-primary-nav rounded-b-border-radius-md z-z-index-tooltip ">
            <div
              ref={listRef}
              className={`overflow-y-auto ${
                isOpen
                  ? yearDropdownTouched
                    ? "calendarYearDropdown-open"
                    : "h-height-base"
                  : yearDropdownTouched
                    ? "calendarYearDropdown-close"
                    : "h-height-base"
              }`}
            >
              <div className="flex flex-col gap-gap-sm pt-padding-sm pb-padding-xs px-padding-sm">
                {options.map((val) => {
                  const isSelected = selectedOption?.value === val.value;

                  return (
                    <div
                      key={val.value}
                      data-value={val.value}
                      className={`flex justify-between items-center px-padding-sm py-padding-button-s relative z-z-index-header rounded-border-radius-md cursor-pointer w-width-full global-hover-white ${
                        isSelected ? "bg-primary-card" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOption({
                          name: val?.name,
                          value: val?.value,
                        });
                        setCurrentYear(val?.value);
                        setOpenDropdown(null);
                      }}
                    >
                      <div className="flex gap-gap-xs justify-between items-center w-auto">
                        <div
                          className={`text-font-xs text-text-primary whitespace-nowrap overflow-ellipsis overflow-hidden w-width-full`}
                        >
                          {val.name}
                        </div>
                      </div>

                      {/* Checkmark */}
                      {isSelected ? (
                        <SVGComponent
                          url={ICONS?.CHECK_MARK_WHITE}
                          color={"var(--text-primary)"}
                          height={"var(--icon-xs)"}
                          width={"var(--icon-xs)"}
                          alt="Arrow"
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            {isOpen ? (
              <div className="pb-padding-sm pt-padding-sm w-width-full rounded-b-border-radius-sm"></div>
            ) : null}
          </div>
        </div>
      );
    },
  );

  // While the calendar is open, the header shows the live (unconfirmed)
  // selection. When closed, it shows the last confirmed value.
  const displayStartDate = showDatePicker ? tempStartDate : localStartDate;
  const displayEndDate = showDatePicker ? tempEndDate : localEndDate;
  const calenderHeight = 330;

  return (
    <div
      className={` relative h-auto flex flex-col ${
        expandWithinContainer
          ? showDatePicker
            ? "gap-gap-sm"
            : "gap-0"
          : "gap-gap-sm"
      } w-width-full ${isFullWidthRequired ? "w-width-full" : type === "big" ? "Qsmd:max-w-calendar-width-big" : "Qsmd:max-w-calendar-select-width-small"} `}
      ref={datePickerRef}
    >
      <div
        className={`flex justify-between items-center gap-gap-sm rounded-border-radius-md py-padding-sm px-padding-md bg-primary-nav-act w-width-full relative z-z-index-header ${isDisabled ? "opacity-light-opacity cursor-not-allowed" : "cursor-pointer global-hover-white"} ${isFullWidthRequired ? "w-width-full" : type === "big" ? "Qsmd:max-w-calendar-width-big" : "Qsmd:max-w-calendar-select-width-small"}`}
        onClick={() => {
          if (isDisabled) return;
          const containerRect = document
            .getElementById("root")
            ?.getBoundingClientRect();
          const cardRect = dropdownOptionRef?.current?.getBoundingClientRect();
          const willOverflowBottom =
            cardRect?.bottom > containerRect?.bottom &&
            cardRect?.top >= calenderHeight;
          // setOpenUpward(willOverflowBottom);
          setShowDatePicker(!showDatePicker);
          setYearDropdownTouched(false); // user interacted with the dropdown
          setOpenDropdown(null);
          setTempStartDate(startDate);
          setTempEndDate(endDate || null);
          // setLocalStartDate(startDate);
          // setLocalEndDate(endDate || null);
          resetToStartDate();

          if (!localStartDate) {
            setCurrentYear(today.getFullYear());
          }
          // FORCE REMOUNT
          setDpKey((prev) => prev + 1);
          if (!startDate) {
            if (effectiveMin) {
              setCurrentView(effectiveMin);
              setCurrentMonth(effectiveMin.getMonth());
              setCurrentYear(effectiveMin.getFullYear());
            } else {
              resetToToday();
            }
          }
        }}
      >
        {/* Start date text */}
        <div
          className={`${type === "big" ? "text-font-md" : "text-font-xs"} ${
            localStartDate ? "text-text-primary" : "text-text-secondary"
          } flex-1`}
        >
          {displayStartDate
            ? formatDate(displayStartDate)
            : isRange
              ? TEXT.FROM_DATE
              : TEXT.SELECT_DATE}
        </div>

        {!isRange && (
          <div
            className={`transition-transform duration-animation-fast ease-in-out ${
              showDatePicker ? "rotate-half" : "rotate-base"
            }`}
          >
            <SVGComponent
              url={ICONS?.ARROW_DOWN_WHITE}
              color="var(--text-primary)"
              width="var(--icon-xs)"
              height="var(--icon-xs)"
            />
          </div>
        )}

        {isRange ? (
          <>
            <SVGComponent
              url={ICONS?.ARROW_RIGHT_GREY}
              width={"var(--icon-sm)"}
              height={"var(--icon-sm)"}
              alt={"Arrow Right Grey"}
              color={"var(--text-primary)"}
            />
            {/* End date text */}
            <div
              className={`${type === "big" ? "text-font-md" : "text-font-xs"} ${
                localEndDate ? "text-text-primary" : "text-text-secondary"
              } flex flex-1 justify-end`}
            >
              {displayEndDate ? formatDate(displayEndDate) : TEXT.TO_DATE}
            </div>
          </>
        ) : null}
      </div>
      <div
        // ref={dropdownOptionRef}
        className={`${
          expandWithinContainer
            ? `relative overflow-hidden transition-[max-height,opacity,transform] duration-animation-fast ease-in-out ${
                showDatePicker
                  ? "max-h-max-height-for-calendar-animation opacity-base-opacity translate-y-0"
                  : "max-h-0 opacity-opacity-none -translate-y-1"
              }`
            : `absolute w-width-full shadow-box-shadow-all z-z-index-dropdown transition-[max-height,opacity] duration-animation-fast-s ease-in-out rounded-border-radius-md ${showDatePicker ? "max-h-max-height-for-calendar-animation opacity-base-opacity" : "max-h-[var(--height-base)] opacity-opacity-none"}`
        }
        ${`absolute w-width-full top-[calc(var(--top-full)+var(--padding-sm))] shadow-box-shadow-all z-z-index-dropdown transition-[max-height,opacity] duration-animation-fast-s ease-in-out rounded-border-radius-md ${
          showDatePicker
            ? "max-h-max-height-for-calendar-animation opacity-base-opacity"
            : "max-h-[var(--height-base)] opacity-opacity-none"
        }`}`}
      >
        <div
          className={`relative ${
            expandWithinContainer ? "flex" : showDatePicker ? "flex" : "hidden"
          } flex-col bg-primary-nav-act rounded-border-radius-md p-padding-md w-width-full shadow-box-shadow-all ${
            type === "big" ? "calendar-big" : "calendar-small"
          }`}
          style={
            expandWithinContainer
              ? {
                  pointerEvents: showDatePicker ? "auto" : "none",
                }
              : {}
          }
        >
          <DatePicker
            key={dpKey}
            inline
            // showYearDropdown
            focusSelectedMonth={true}
            minDate={effectiveMin}
            maxDate={effectiveMax}
            openToDate={resolvedOpenDate}
            onMonthChange={handleMonthChange}
            // RANGE MODE
            selected={tempStartDate}
            selectsRange={isRange}
            startDate={tempStartDate}
            endDate={isRange ? tempEndDate : null}
            onChange={(date) => handleChange(date)}
            dayClassName={(date) => getDayClass(date)}
            renderCustomHeader={({
              date,
              decreaseMonth,
              increaseMonth,
              changeYear,
            }) => {
              const year = currentYear;

              const monthName = date.toLocaleString("default", {
                month: "long",
              });

              return (
                <div className="flex flex-col items-center gap-gap-sm pb-padding-sm border-b-border border-b-primary-bg">
                  {/* Top Row: Arrows + Month */}
                  <div className="flex justify-between items-center w-width-full p-padding-xs">
                    <button
                      onClick={!hidePrev ? decreaseMonth : undefined}
                      style={{
                        opacity: hidePrev
                          ? "var(--light-opacity)"
                          : "var(--base-opacity)",
                      }}
                      className="rotate-180"
                    >
                      <SVGComponent
                        url={ICONS?.ARROW_RIGHT_GREY}
                        width={"var(--icon-sm)"}
                        height={"var(--icon-sm)"}
                        alt={"Arrow Left"}
                        color={"var(--text-primary)"}
                      />
                    </button>
                    <span className="text-text-primary text-font-xs">
                      {monthName}
                    </span>
                    <button
                      onClick={!hideNext ? increaseMonth : undefined}
                      style={{
                        opacity: hideNext
                          ? "var(--light-opacity)"
                          : "var(--base-opacity)",
                      }}
                    >
                      <SVGComponent
                        url={ICONS?.ARROW_RIGHT_GREY}
                        width={"var(--icon-sm)"}
                        height={"var(--icon-sm)"}
                        alt={"Arrow Right"}
                        color={"var(--text-primary)"}
                      />
                    </button>
                  </div>
                  {/* YEAR DROPDOWN */}
                  <Dropdown
                    year={year}
                    options={[
                      ...(() => {
                        const today = new Date();
                        const currentYear = today.getFullYear();

                        let years = [];

                        if (effectiveMin) {
                          // CASE 1: minDate given, ascending from minYear, minYear + 99
                          const minYear = effectiveMin.getFullYear();
                          const maxAllowed = Math.min(
                            minYear + 100,
                            effectiveMax
                              ? effectiveMax.getFullYear()
                              : minYear + 100,
                          );

                          years = Array.from(
                            { length: maxAllowed - minYear + 1 },
                            (_, i) => minYear + i,
                          );
                        } else if (maxDate === null) {
                          // CASE 2: no maxDate → show past 100 and future 100 years
                          const centerYear = effectiveMax
                            ? effectiveMax.getFullYear()
                            : currentYear;

                          const startYear = centerYear - 100;
                          const endYear = centerYear + 100;

                          years = Array.from(
                            { length: endYear - startYear + 1 },
                            (_, i) => startYear + i,
                          );
                        } else {
                          // CASE 3: no minDate, last 100 years (descending)
                          const maxYear = Math.min(
                            currentYear,
                            effectiveMax
                              ? effectiveMax.getFullYear()
                              : currentYear,
                          );

                          years = Array.from(
                            { length: 101 },
                            (_, i) => maxYear - i,
                          );
                        }

                        return years.map((y) => ({
                          name: y,
                          value: y,
                        }));
                      })(),
                    ]}
                    selectedOption={{
                      name: year,
                      value: year,
                    }}
                    setSelectedOption={(opt) => changeYear(opt.value)}
                    isOpen={openDropdown === "year"}
                    dropdownKey="year"
                    setOpenDropdown={setOpenDropdown}
                    dropdownRef={yearDropdownRef}
                  />
                </div>
              );
            }}
            renderDayContents={(day, date) => {
              const formattedDay = String(day).padStart(2, "0");
              return <span>{formattedDay}</span>;
            }}
          />

          <div className="w-width-full flex gap-gap-md">
            <PrimaryButton
              btnText={TEXT?.CLEAR}
              handleFunction={handleClear}
              enabledTextColor="var(--text-primary)"
              disabledTextColor="var(--text-primary)"
              enabledBgColor="var(--primary-nav-act)"
              disabledBgColor="var(--primary-nav-act)"
              outLineColor="var(--text-primary)"
              hoverOption="white"
              isDisabled={!tempStartDate}
              fullWidthRequired
            />

            <PrimaryButton
              btnText={TEXT?.CONFIRM}
              handleFunction={handleConfirm}
              enabledTextColor="var(--primary-bg)"
              disabledTextColor="var(--text-primary)"
              enabledBgColor="var(--text-primary)"
              disabledBgColor="var(--text-disabled)"
              hoverOption="black"
              isDisabled={!tempStartDate}
              // isDisabled={
              //   isRange ? !tempStartDate || !tempEndDate : !tempStartDate
              // }
              fullWidthRequired
              inActiveVariantNeeded
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonCalendar;
